<template>
  <a-modal
    :visible="modalVisible"
    title="Plantillas de correo"
    width="1000px"
    centered
    @cancel="handleConfirmationClose"
    wrapClassName="modal-divider email-templates-modal"
  >
    <div class="modal-content">
      <div class="modal-left">
        <p style="margin-bottom: 12px;">
          Selecciona y previsualiza la plantilla que deseas insertar en tu correo.
          Solo se muestran plantillas activas y puedes insertar hasta 3 plantillas por correo.
        </p>
        <a-input
          v-model:value="searchTemplate"
          placeholder="Buscar nombre de plantilla..."
          style="margin-bottom: 16px;"
        >
          <template #prefix>
            <SearchOutlined style="color: #00000073" />
          </template>
        </a-input>
        <div v-if="filteredPortfolioWithTemplates.length > 0">
          <div style="display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 8px;">
            <span style="color: #00000073">Mostrando {{ totalTemplates }} plantillas activas</span>
            <span style="color: #000000A6; font-style: italic;"
              >{{ totalTemplatesInEmailCount }} de {{ MAX_SELECTED_TEMPLATES }} plantillas insertadas</span
            >
          </div>
          <CustomScrollbar height="410px" class="templates-scrollbar">
            <div>
              <a-collapse v-model:activeKey="activeKey">
                <a-collapse-panel
                  v-for="portfolio in filteredPortfolioWithTemplates"
                  :key="String(portfolio.id)"
                >
                  <template #header>
                    Campaña: {{ portfolio.name }} ({{ portfolio.templates_active_count }})
                  </template>
                  <div
                    v-for="template in portfolio.templates_active"
                    :key="template.id"
                    class="item__template"
                    :class="{ 'item__template--disabled': isTemplateSelectionDisabled(portfolio.id, template.id) }"
                  >
                    <a-checkbox
                      :checked="selectedTemplateKeys.includes(getTemplateSelectionKey(portfolio.id, template.id))"
                      :disabled="isTemplateSelectionDisabled(portfolio.id, template.id)"
                      @change="toggleTemplateSelection(portfolio.id, template.id)"
                    />
                    <div class="item__template-content">
                      <p class="text__name">
                        {{ template.name }}
                      </p>
                      <a-tooltip
                        :title="template.description"
                        :arrow="false"
                        overlayClassName="custom-tooltip"
                        placement="bottomLeft"
                      >
                        <p
                          class="text__alias"
                          :class="{ 'text__alias--disabled': isTemplateSelectionDisabled(portfolio.id, template.id) }"
                        >
                          {{ template.description }}
                        </p>
                      </a-tooltip>
                      <a-tag
                        v-if="getInsertedTemplateCount(portfolio.id, template.id) > 0"
                        color="success"
                        style="margin-top: 8px;"
                      >
                        {{ getInsertedTemplateLabel(portfolio.id, template.id) }}
                      </a-tag>
                    </div>
                    <a-button 
                      type="link" 
                      class="button-link" 
                      :disabled="isTemplateSelectionDisabled(portfolio.id, template.id)"
                      @click="handlePreviewTemplate(template)"
                    >
                      Previsualizar
                    </a-button
                    >
                  </div>
                </a-collapse-panel>
              </a-collapse>
            </div>
          </CustomScrollbar>
        </div>
        <div v-else class="empty-data">
          <SearchOutlined style="font-size: 48px; margin-bottom: 12px;"/>
          <p>No se encontraron resultados a tu búsqueda, <br> prueba con un nombre diferente.</p>
        </div>
      </div>
      <div class="modal-right">
        <div v-if="displayEmailTemplate">
          <a-spin :spinning="!isPreviewReady" size="small">
            <iframe
              :srcdoc="displayEmailTemplate"
              id="template_email"
              title="template_email"
              ref="iframe"
              style="border: none; width: 360px; height: 512px"
              :style="{ visibility: isPreviewReady ? 'visible' : 'hidden' }"
              @load="scaleIframeContent"
            ></iframe>
          </a-spin>
        </div>
        <div v-else>
          <img src="@/app/emails/assets/mail-template.png" alt="mail-template">
          <p>Selecciona una plantilla para <br> previsualizar su contenido.</p>
        </div>
      </div>
    </div>
    <template #footer>
      <a-button key="back" @click="handleConfirmationClose">Cancelar</a-button>
      <span style="margin-inline-start: 8px;">
        <a-tooltip>
          <template #title v-if="disabled">
            Selecciona al menos una plantilla para insertarla dentro del cuerpo de correo.
          </template>
          <a-button key="submit" type="primary" :disabled="disabled" :loading="loading" @click="handleOk">Insertar plantilla</a-button>
        </a-tooltip>
      </span>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch, createVNode } from "vue";
import { SearchOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import type { PortfolioWithTemplates } from "@/@types/emails";
import { TemplateType } from "@/@types/flows/emailTemplates";

import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { portfolioService } from "@/app/entities/services/Portfolio.service";
import { Modal } from "ant-design-vue";
import { buildTemplateBodyHtml, escapeHtmlText, prepareTemplateHtml } from "@/app/emails/utils/templateHtml";

const props = withDefaults(
  defineProps<{
    visible: boolean;
    editorHtml?: string;
  }>(),
  {
    visible: false,
    editorHtml: "",
  }
);

const emit = defineEmits<{
  "update:visible": [value: boolean];
  "insert-templates": [templatesHtml: string, templatesCount: number];
  "template-limit-reached": [value: boolean];
}>();

const modalVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit("update:visible", value),
});

const MAX_SELECTED_TEMPLATES = 3;

const searchTemplate = ref("");
const selectedTemplateKeys = ref<string[]>([]);
const activeKey = ref<string[]>([]);
const loading = ref<boolean>(false);
const displayEmailTemplate = ref("");
const isPreviewReady = ref(false);
const previewTemplateType = ref<number | null>(null);
const disabled = computed(() => selectedTemplatesCount.value === 0);
const portfolioWithTemplates = ref<PortfolioWithTemplates[]>([]);
const iframe = ref();

const getTemplateSelectionKey = (portfolioId: number, templateId: number) => {
  return `${templateId}-${portfolioId}`;
};

const insertedTemplateCounters = computed<Record<string, number>>(() => {
  const counters: Record<string, number> = {};
  const html = props.editorHtml || "";

  if (!html.trim() || typeof DOMParser === "undefined") return counters;

  try {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const insertedTemplateCards = parsed.querySelectorAll<HTMLElement>('[data-template-card="email"]');

    insertedTemplateCards.forEach((templateCard) => {
      const templateId = Number(templateCard.getAttribute("data-template-id"));
      const portfolioId = Number(templateCard.getAttribute("data-portfolio-id"));
      if (!Number.isFinite(templateId) || !Number.isFinite(portfolioId)) return;

      const selectionKey = getTemplateSelectionKey(portfolioId, templateId);
      counters[selectionKey] = (counters[selectionKey] || 0) + 1;
    });
  } catch (error) {
    console.error("Error extracting inserted templates:", error);
  }

  return counters;
});

const getInsertedTemplateCount = (portfolioId: number, templateId: number) => {
  const selectionKey = getTemplateSelectionKey(portfolioId, templateId);
  return insertedTemplateCounters.value[selectionKey] || 0;
};

const insertedTemplatesCount = computed(() => {
  return Object.values(insertedTemplateCounters.value).reduce((total, count) => total + count, 0);
});

const isTemplateLimitReached = computed(() => {
  return insertedTemplatesCount.value >= MAX_SELECTED_TEMPLATES;
});

const getInsertedTemplateLabel = (portfolioId: number, templateId: number) => {
  const insertedCount = getInsertedTemplateCount(portfolioId, templateId);
  return insertedCount === 1 ? "Insertada 1 vez" : `Insertada ${insertedCount} veces`;
};

const buildEditableTemplateHtml = (templateHtml: string) => {
  if (!templateHtml?.trim()) return "";
  // Solo extraer body + estilos, SIN inlinear CSS, SIN fluid, SIN envolturas
  try {
    const doc = new DOMParser().parseFromString(templateHtml, "text/html");
    const styles = Array.from(doc.querySelectorAll("style")).map((s) => s.outerHTML).join("");
    const bodyContent = doc.body?.innerHTML || templateHtml;
    return `${styles}\n${bodyContent}`.trim();
  } catch {
    return templateHtml;
  }
};

const buildTemplateCardKey = (selectionKey: string) => {
  const randomSuffix = Math.random().toString(36).slice(2, 8);
  return `${selectionKey}-${Date.now().toString(36)}-${randomSuffix}`;
};

const buildTemplateCardHtml = (
  templateName: string,
  templateHtml: string,
  templateCardKey: string,
  templateId: number,
  portfolioId: number
) => {
  const editableTemplateHtml = buildEditableTemplateHtml(templateHtml);
  if (!editableTemplateHtml) return "";

  const safeTemplateName = escapeHtmlText(templateName.trim() || "Sin nombre");
  const safeTemplateCardKey = escapeHtmlText(templateCardKey);

  return `
    <div data-template-card="email" data-template-card-key="${safeTemplateCardKey}" data-template-id="${templateId}" data-portfolio-id="${portfolioId}" style="margin: 12px 0; border: 1px solid #D9D9D9; border-radius: 6px; overflow: hidden; background: #FFFFFF;">
      <div data-template-card-header contenteditable="false" style="display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; background: #FAFAFA; border-bottom: 1px solid #F0F0F0; font-size: 12px; line-height: 1.4; color: #595959;">
        <span style="font-weight: 500; color: #434343;">Plantilla: ${safeTemplateName}</span>
        <span data-template-card-remove="true" data-template-card-key="${safeTemplateCardKey}" style="color: #1677FF; cursor: pointer;">Quitar</span>
      </div>
      <div data-template-card-body style="padding: 10px 12px;">
        ${editableTemplateHtml}
      </div>
    </div>
  `.trim();
};

const selectedTemplatesHtml = computed(() => {
  return selectedTemplateKeys.value
    .map((selectionKey) => {
      const [templateIdRaw, portfolioIdRaw] = selectionKey.split("-");
      const templateId = Number(templateIdRaw);
      const portfolioId = Number(portfolioIdRaw);

      const portfolio = portfolioWithTemplates.value.find((item) => item.id === portfolioId);
      const template = portfolio?.templates_active.find((item) => item.id === templateId);

      return buildTemplateCardHtml(
        template?.name ?? "",
        template?.template ?? "",
        buildTemplateCardKey(selectionKey),
        templateId,
        portfolioId
      );
    })
    .filter((templateHtml) => Boolean(templateHtml));
});

const filteredPortfolioWithTemplates = computed(() => {
  const search = searchTemplate.value.trim().toLowerCase();

  if (!search) return portfolioWithTemplates.value;

  return portfolioWithTemplates.value
    .map((portfolio) => {
      const filteredTemplates = portfolio.templates_active.filter((template) =>
        template.name.toLowerCase().includes(search)
      );

      return {
        ...portfolio,
        templates_active_count: filteredTemplates.length,
        templates_active: filteredTemplates,
      };
    })
    .filter((portfolio) => portfolio.templates_active.length > 0);
});

const totalTemplates = computed(() => {
  return filteredPortfolioWithTemplates.value.reduce(
    (total, portfolio) => total + portfolio.templates_active.length,
    0
  );
});

const selectedTemplatesCount = computed(() => {
  return selectedTemplateKeys.value.length;
});

const availableSelectionSlots = computed(() => {
  return Math.max(MAX_SELECTED_TEMPLATES - insertedTemplatesCount.value, 0);
});

const totalTemplatesInEmailCount = computed(() => {
  return insertedTemplatesCount.value + selectedTemplatesCount.value;
});

const isTemplateSelectionDisabled = (portfolioId: number, templateId: number) => {
  if (loading.value) return true;

  const selectionKey = getTemplateSelectionKey(portfolioId, templateId);
  return (
    !selectedTemplateKeys.value.includes(selectionKey) &&
    selectedTemplatesCount.value >= availableSelectionSlots.value
  );
};

const getDefaultExpandedKeys = (keys: string[]) => {
  return keys.slice(0, 3);
};

const toggleTemplateSelection = (portfolioId: number, templateId: number) => {
  const selectionKey = getTemplateSelectionKey(portfolioId, templateId);
  const index = selectedTemplateKeys.value.indexOf(selectionKey);

  if (index >= 0) {
    selectedTemplateKeys.value.splice(index, 1);
    return;
  }

  if (selectedTemplateKeys.value.length < availableSelectionSlots.value) {
    selectedTemplateKeys.value.push(selectionKey);
  }
};

const getPortfolioWithTemplates = async () => {
  try {
    const response = await portfolioService.getPortfolioWithTemplates();

    portfolioWithTemplates.value = response.filter(
      (portfolio) => portfolio.templates_active_count > 0
    );

    activeKey.value = getDefaultExpandedKeys(
      portfolioWithTemplates.value.map((portfolio) => String(portfolio.id))
    );
  } catch (error) {
    console.error(error);
    portfolioWithTemplates.value = [];
    activeKey.value = [];
  }
};

const handlePreviewTemplate = async (template: { template: string; template_type?: number }) => {
  const templateHtml = prepareTemplateHtml(template.template || "");

  previewTemplateType.value = Number(template.template_type) || null;
  isPreviewReady.value = false;
  if (displayEmailTemplate.value === templateHtml) {
    displayEmailTemplate.value = "";
    await nextTick();
  }

  displayEmailTemplate.value = templateHtml;
};

const scaleIframeContent = () => {
  const scale = 0.47;
  const iframeDocument = iframe.value?.contentWindow?.document;

  if (!iframeDocument?.body || !iframe.value) return;

  if (previewTemplateType.value === TemplateType.Html) {
    iframe.value.style.backgroundColor = "transparent";
    const iframeWindow = iframeDocument.defaultView || window;
    const htmlBackground = iframeWindow.getComputedStyle(iframeDocument.documentElement).backgroundColor;
    const bodyBackground = iframeWindow.getComputedStyle(iframeDocument.body).backgroundColor;
    const backgroundColor =
      bodyBackground && bodyBackground !== "transparent" && bodyBackground !== "rgba(0, 0, 0, 0)"
        ? bodyBackground
        : htmlBackground;
    iframeDocument.documentElement.style.background = "transparent";
    iframeDocument.body.style.background = `linear-gradient(${backgroundColor}, ${backgroundColor}) top left / 100% ${iframeDocument.body.scrollHeight}px no-repeat`;
  } else {
    const iframeWindow = iframeDocument.defaultView || window;
    const htmlBackground = iframeWindow.getComputedStyle(iframeDocument.documentElement).backgroundColor;
    const bodyBackground = iframeWindow.getComputedStyle(iframeDocument.body).backgroundColor;
    const isTransparent = (color: string) =>
      color === "transparent" || color === "rgba(0, 0, 0, 0)";

    iframe.value.style.removeProperty("background-color");

    if (isTransparent(htmlBackground) && isTransparent(bodyBackground)) {
      // Evita que el fondo del body se propague a todo el canvas del iframe.
      iframeDocument.documentElement.style.backgroundImage =
        "linear-gradient(transparent, transparent)";
      iframeDocument.documentElement.style.backgroundColor = "transparent";
      iframeDocument.body.style.backgroundColor = "#FFFFFF";
    }
  }

  iframeDocument.body.style.zoom = scale;
  requestAnimationFrame(() => {
    isPreviewReady.value = true;
  });
};

onMounted(() => {
  getPortfolioWithTemplates();
});

watch(
  () => modalVisible.value,
  (isVisible) => {
    if (!isVisible) return;

    activeKey.value = getDefaultExpandedKeys(
      portfolioWithTemplates.value.map((portfolio) => String(portfolio.id))
    );
  }
);

watch(
  () => isTemplateLimitReached.value,
  (value) => {
    emit("template-limit-reached", value);
  },
  { immediate: true }
);

const handleConfirmationClose = () => {
  Modal.confirm({
    title: "¿Estás seguro de descartar cambios?",
    content: "Al descartar, no se guardarán los datos previamente seleccionados.",
    icon: createVNode(QuestionCircleOutlined),
    onOk: () => handleCancel(),
    centered: true,
    okText: "Sí, descartar",
    cancelText: "No, cancelar",
  });
};

const handleCancel = () => {
  selectedTemplateKeys.value = [];
  displayEmailTemplate.value = "";
  previewTemplateType.value = null;
  isPreviewReady.value = false;
  modalVisible.value = false;
};

const handleOk = () => {
  loading.value = true;
  setTimeout(() => {
    const templatesHtml = selectedTemplatesHtml.value;

    emit("insert-templates", templatesHtml.join("<p></p>"), templatesHtml.length);
    selectedTemplateKeys.value = [];
    loading.value = false;
    displayEmailTemplate.value = "";
    previewTemplateType.value = null;
    isPreviewReady.value = false;
    modalVisible.value = false;
  }, 2000);
};
</script>

<style scoped lang="sass">
.modal-content
  display: flex
  align-items: stretch

.modal-left
  width: 600px
  padding: 20px 24px 0 24px

.modal-right
  width: 400px
  background: #F0F5FF
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center
  text-align: center

:deep(.templates-scrollbar)
  padding-right: 12px
  margin-right: -12px

:deep(.templates-scrollbar .ps__rail-y)
  right: 2px !important

.text__name
  overflow: hidden
  white-space: nowrap
  text-overflow: ellipsis
  margin: 0
  font-weight: 600

.text__alias
  overflow: hidden
  white-space: nowrap
  text-overflow: ellipsis
  margin: 0
  color: #000000A6
  font-size: 12px

.text__alias--disabled
  color: #00000040

.item__template
  display: flex
  border-radius: 10px
  align-items: center
  gap: 12px
  padding: 12px 20px
  border: 1px solid $gray-4

  &:not(:last-child)
    margin-bottom: 12px

.item__template--disabled
  background: #0000000A
  color: #00000040 
  cursor: not-allowed
  user-select: none
  *
    pointer-events: none

.item__template-content
  flex: 1
  min-width: 0

.button-link
  margin-left: auto
  padding: 0
  height: auto

.empty-data
  color: #00000040
  min-height: 436px
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center
  padding: 0 16px
  text-align: center

</style>

<style>
.email-templates-modal .ant-modal-body {
  padding: 0 !important;
}
</style>

